import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
  ChallengeView,
  Project,
  ProjectAlert,
  ProjectView,
  ProjectViewRepeatingTaskHistoryDetailParams,
  ProjectViewRoutineDetail,
  RemoveUserConflictResponse,
} from '../model/project';
import {ProjectTask, TaskTemplate} from '../model/task';
import {TrackerMetricTypes} from '../../trackers/shared/tracker-metric-types';

@Injectable({
  providedIn: 'root',
})
export class ProjectsApiService {
  constructor(private http: HttpClient) {}

  private get url() {
    return `${environment.apiUrl}/projects`;
  }

  private get tasksUrl() {
    return `${environment.apiUrl}/tasks`;
  }

  get(): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.url}`);
  }

  getSummary(): Observable<Partial<Project>[]> {
    return this.http.get<Partial<Project>[]>(`${this.url}/summary`);
  }

  getSingle(id: string): Observable<Project> {
    return this.http.get<Project>(`${this.url}/${id}`);
  }

  create(project: Partial<Project>): Observable<Project> {
    return this.http.post<Project>(`${this.url}`, project);
  }

  patch(project: Project): Observable<Project> {
    return this.http.patch<Project>(`${this.url}/${project._id}/scheduleProject`, project);
  }

  put(project: Project): Observable<Project> {
    return this.http.put<Project>(`${this.url}/${project._id}`, project);
  }

  duplicate(id: string): Observable<Project> {
    return this.http.post<Project>(`${this.url}/${id}`, null);
  }

  delete(id: string): Observable<Object> {
    return this.http.delete(`${this.url}/${id}`);
  }

  addOrMoveProjectTask(
    projectId: string,
    stepId: string,
    taskIndex: number,
    task: ProjectTask,
    add: boolean
  ): Observable<Project> {
    const urlSuffix = add ? 'addTask' : 'moveTask';
    const payload = {
      stepId,
      task,
      index: taskIndex,
    };
    return this.http.patch<Project>(`${this.url}/${projectId}/${urlSuffix}`, payload);
  }

  updateProjectTask(projectId: string, stepId: string, task: ProjectTask): Observable<Project> {
    return this.http.patch<Project>(`${this.url}/${projectId}/updateTask`, {stepId, task});
  }

  deleteProjectTask(projectId: string, taskInstanceId: string): Observable<Project> {
    return this.http.patch<Project>(`${this.url}/${projectId}/removeTask/${taskInstanceId}`, {});
  }

  getProjectView(projectId: string, params: Record<string, string>): Observable<ProjectView> {
    return this.http.get<ProjectView>(`${this.url}/viewer/${projectId}`, {params: {...params}});
  }

  getRoutineDetail(
    id: string,
    params: ProjectViewRepeatingTaskHistoryDetailParams
  ): Observable<ProjectViewRoutineDetail> {
    return this.http.get<ProjectViewRoutineDetail>(`${this.url}/viewer/routines/${id}`, {params: {...params}});
  }

  getTaskTemplates(search?: string): Observable<TaskTemplate[]> {
    const params = search ? {label: search} : {};
    return this.http.get<TaskTemplate[]>(`${this.tasksUrl}/templates`, {params});
  }

  createTaskTemplate(task: TaskTemplate): Observable<ProjectTask> {
    return this.http.post<ProjectTask>(`${this.tasksUrl}/templates`, task);
  }

  updateTaskTemplate(task: TaskTemplate): Observable<ProjectTask> {
    return this.http.put<ProjectTask>(`${this.tasksUrl}/templates/${task._id}`, task);
  }

  deleteTaskTemplate(id: string) {
    return this.http.delete(`${this.tasksUrl}/templates/${id}`);
  }

  createStep(project: Project): Observable<Project> {
    // this will change
    return this.http.put<Project>(`${this.url}/${project._id}`, project);
  }

  updateStep(project: Project): Observable<Project> {
    // this will change
    return this.http.put<Project>(`${this.url}/${project._id}`, project);
  }

  deleteStep(project: Project): Observable<Project> {
    // this will change
    return this.http.put<Project>(`${this.url}/${project._id}`, project);
  }

  createAlert(alert: ProjectAlert, stepId: string, projectId: string): Observable<Project> {
    return this.http.patch<Project>(`${this.url}/${projectId}/addAlert`, {alert, stepId});
  }

  updateAlert(alert: ProjectAlert, stepId: string, projectId: string): Observable<Project> {
    return this.http.patch<Project>(`${this.url}/${projectId}/updateAlert`, {alert, stepId});
  }

  deleteAlert(alertId: string, projectId: string): Observable<Project> {
    return this.http.patch<Project>(`${this.url}/${projectId}/removeAlert/${alertId}`, {});
  }

  assignUserOrTeam(projectId: string, userId?: string, teamId?: string): Observable<Project> {
    const body: {userId?: string; teamId?: string} = {};
    if (userId) {
      body.userId = userId;
    }
    if (teamId) {
      body.teamId = teamId;
    }
    return this.http.patch<Project>(`${this.url}/${projectId}/assignUserOrTeam`, body);
  }

  removeUserOrTeam(
    projectId: string,
    userId?: string,
    teamId?: string,
    force?: boolean
  ): Observable<Project | RemoveUserConflictResponse> {
    const body: {userId?: string; teamId?: string} = {};
    if (userId) {
      body.userId = userId;
    }
    if (teamId) {
      body.teamId = teamId;
    }

    let url = `${this.url}/${projectId}/removeUserOrTeam`;
    if (force) {
      url += '?force=true';
    }

    return this.http.patch<Project>(url, body);
  }

  updateChallengeCycleSuccessThreshold(
    projectId: string,
    cycleNumber: number,
    cycleSuccessThreshold: number
  ): Observable<void> {
    return this.http.patch<void>(`${this.url}/${projectId}/challenge/cycles/${cycleNumber}/cycleSuccessThreshold`, {
      cycleSuccessThreshold,
    });
  }

  getProjectChallenge(projectId: string): Observable<ChallengeView> {
    // TODO: Replace stub with real API call: return this.http.get<ChallengeView>(`${this.url}/${projectId}/challenge`);
    const stub: ChallengeView = {
      _id: '69af83a0d66878b44de5fca6',
      currentCycle: [
        {successful: true, active: false},
        {successful: true, active: false},
        {successful: false, active: false},
        {successful: true, active: true},
        {successful: false, active: false},
        {successful: false, active: false},
        {successful: false, active: false},
      ],
      level: {
        levelNumber: 3,
        streakCount: 5,
        items: [
          {successful: true, active: false},
          {successful: true, active: false},
          {successful: true, active: true},
        ],
      },
      tasks: [
        {_id: 'task-1', label: 'Morning Workout', schedule: 'morning', icon: 'alarm'},
        {_id: 'task-2', label: 'Meal Prep', schedule: 'afternoon', icon: 'apple'},
        {_id: 'task-3', label: 'Cardio Session', schedule: 'evening', icon: 'memo'},
        {_id: 'task-4', label: 'Listen to a podcast', schedule: 'evening', icon: 'memo', isSalvage: true},
        {_id: 'task-5', label: 'Morning Workout', schedule: 'morning', icon: 'alarm'},
        {_id: 'task-6', label: 'Meal Prep', schedule: 'afternoon', icon: 'apple'},
        {_id: 'task-7', label: 'Cardio Session', schedule: 'evening', icon: 'memo'},
        {_id: 'task-8', label: 'Listen to a podcast', schedule: 'evening', icon: 'memo', isSalvage: true},
      ],
      metrics: [
        {
          _id: 'metric-1',
          name: 'Calories Burned',
          type: TrackerMetricTypes.Numeric,
          typeOption: 'calories',
          target: {
            _id: 'target-1',
            targetName: 'Burn 500 cal/day',
            targetValue: 500,
            targetOperator: '>=',
            targetPeriod: 'perDay',
            targetSuccessThreshold: 80,
            metricId: 'metric-1',
            taskId: 'task-1',
          },
          taskId: 'task-1',
        },
        {
          _id: 'metric-2',
          name: 'Protein Intake',
          type: TrackerMetricTypes.Numeric,
          typeOption: 'grams',
          target: {
            _id: 'target-2',
            targetName: '150g protein/day',
            targetValue: 150,
            targetOperator: '>=',
            targetPeriod: 'perDay',
            targetSuccessThreshold: 80,
            metricId: 'metric-2',
            taskId: 'task-2',
          },
          taskId: 'task-2',
        },
        {
          _id: 'metric-3',
          name: 'Time walked',
          type: TrackerMetricTypes.Duration,
          typeOption: 'hrs/min',
          target: {
            _id: 'target-2',
            targetName: '150g protein/day',
            targetValue: 150,
            targetOperator: '>=',
            targetPeriod: 'perDay',
            targetSuccessThreshold: 80,
            metricId: 'metric-2',
            taskId: 'task-2',
          },
          taskId: 'task-2',
        },
        {
          _id: 'metric-4',
          name: 'Time walked',
          type: TrackerMetricTypes.Duration,
          typeOption: 'min',
          target: {
            _id: 'target-2',
            targetName: '150g protein/day',
            targetValue: 150,
            targetOperator: '>=',
            targetPeriod: 'perDay',
            targetSuccessThreshold: 80,
            metricId: 'metric-2',
            taskId: 'task-2',
          },
          taskId: 'task-2',
        },
        {
          _id: 'metric-5',
          name: 'Took out the trash?',
          type: TrackerMetricTypes.Boolean,
          typeOption: 'times',
          target: {
            _id: 'target-2',
            targetName: '150g protein/day',
            targetValue: 150,
            targetOperator: '>=',
            targetPeriod: 'perDay',
            targetSuccessThreshold: 80,
            metricId: 'metric-2',
            taskId: 'task-2',
          },
          taskId: 'task-2',
        },
      ],
      targets: [
        {
          _id: 'target-1',
          targetName: 'Burn 500 cal/day',
          targetMetric: {trackerId: 'tracker-1', metricId: 'metric-1'},
          targetValue: 500,
          targetOperator: '>=',
          targetPeriod: 'perDay',
          targetSuccessThreshold: 3,
          taskId: 'task-1',
          metricId: 'metric-1',
        },
        {
          _id: 'target-2',
          targetName: '150g protein/day',
          targetMetric: {trackerId: 'tracker-2', metricId: 'metric-2'},
          targetValue: 150,
          targetOperator: '>=',
          targetPeriod: 'perDay',
          targetSuccessThreshold: 4,
          taskId: 'task-2',
          metricId: 'metric-2',
        },
      ],
      milestones: [
        {
          _id: 'ms-1',
          name: 'First Week Complete',
          description: 'Complete your first full week',
          successCount: 7,
        },
        {_id: 'ms-2', name: 'Level 5 Reached', description: 'Reach level 5 in the challenge', successCount: 0},
        {
          _id: 'ms-3',
          name: 'First Week Complete',
          description: 'Complete your first full week',
          successCount: 7,
        },
        {_id: 'ms-4', name: 'Level 5 Reached', description: 'Reach level 5 in the challenge', successCount: 2},
        {
          _id: 'ms-5',
          name: 'First Week Complete',
          description: 'Complete your first full week',
          successCount: 7,
        },
        {_id: 'ms-6', name: 'Level 5 Reached', description: 'Reach level 5 in the challenge', successCount: 10000},
        {
          _id: 'ms-7',
          name: 'First Week Complete',
          description: 'Complete your first full week',
          successCount: 7,
        },
        {_id: 'ms-8', name: 'Level 5 Reached', description: 'Reach level 5 in the challenge', successCount: 0},
        {
          _id: 'ms-9',
          name: 'First Week Complete',
          description: 'Complete your first full week',
          successCount: 7,
        },
        {_id: 'ms-10', name: 'Level 5 Reached', description: 'Reach level 5 in the challenge', successCount: 0},
      ],
      notifications: [
        {
          _id: 'notif-1',
          days: [0, 1, 2, 3, 4],
          time: '2026-01-01T12:00:00Z',
          message: 'Morning Reminder',
          active: true,
        },
        {
          _id: 'notif-2',
          days: [0, 1, 2, 3, 4, 5, 6],
          time: '2026-01-01T22:00:00Z',
          message: 'Evening Check-in',
          active: true,
        },
        {
          _id: 'notif-3',
          days: [0],
          time: '2026-01-01T22:00:00Z',
          message: 'Evening Check-in',
          active: false,
        },
        {
          _id: 'notif-4',
          days: [0, 2, 5, 6],
          time: '2026-01-01T22:00:00Z',
          message: 'Evening Check-in',
          active: false,
        },
      ],
      cycles: [
        {
          cycleNumber: 1,
          current: false,
          percentComplete: 45,
          cycleSuccessThreshold: 50,
          otherUsers: [],
          metrics: [
            {
              metricId: 'metric-1',
              metricName: 'Calories Burned',
              metricPeriod: 'perDay',
              metricType: TrackerMetricTypes.Numeric,
              metricValues: [520, 480, 510, 490, 500, 460, 530],
              targetOperator: '>=',
              targetSuccessThreshold: 3,
              taskIcon: 'birthdaycake',
              targetValue: 500,
              percentComplete: 65,
              uom: 'cal',
            },
            {
              metricId: 'metric-2',
              metricName: 'Protein Intake',
              metricPeriod: 'perDay',
              metricType: TrackerMetricTypes.Numeric,
              metricValues: [160, 145, 155, 130, 170, 140, 150],
              targetOperator: '>=',
              targetSuccessThreshold: 4,
              taskIcon: 'flexed-biceps',
              targetValue: 150,
              percentComplete: 43,
              uom: 'g',
            },
          ],
        },
        {
          cycleNumber: 2,
          current: false,
          percentComplete: 72,
          cycleSuccessThreshold: 50,
          otherUsers: [],
          metrics: [
            {
              metricId: 'metric-1',
              metricName: 'Calories Burned',
              metricPeriod: 'perDay',
              metricType: TrackerMetricTypes.Numeric,
              metricValues: [520, 480, 200, 550, 490, 300, 530],
              targetOperator: '>=',
              targetSuccessThreshold: 3,
              taskIcon: 'exercise-bike',
              targetValue: 500,
              percentComplete: 85,
              uom: 'cal',
            },
            {
              metricId: 'metric-2',
              metricName: 'Protein Intake',
              metricPeriod: 'perDay',
              metricType: TrackerMetricTypes.Numeric,
              metricValues: [160, 145, 155, 100, 140, 150, 78],
              targetOperator: '>=',
              targetSuccessThreshold: 4,
              taskIcon: 'steak',
              targetValue: 150,
              percentComplete: 60,
              uom: 'g',
            },
          ],
        },
        {
          cycleNumber: 3,
          current: true,
          percentComplete: 80,
          cycleSuccessThreshold: 25,
          otherUsers: [],
          metrics: [
            {
              metricId: 'metric-1',
              metricName: 'Calories Burned',
              metricPeriod: 'perDay',
              metricType: TrackerMetricTypes.Numeric,
              metricValues: [100, 45, 110, 0, 0, 0, 0],
              targetOperator: '>=',
              targetSuccessThreshold: 3,
              taskIcon: 'swimming(2)',
              targetValue: 500,
              percentComplete: 65,
              uom: 'calories',
            },
            {
              metricId: 'metric-2',
              metricName: 'Protein Intake',
              metricPeriod: 'perDay',
              metricType: TrackerMetricTypes.Numeric,
              metricValues: [160, 145, 155, 0, 0, 0, 0],
              targetOperator: '>=',
              targetSuccessThreshold: 4,
              taskIcon: 'apple',
              targetValue: 150,
              percentComplete: 43,
              uom: 'grams',
            },
          ],
        },
      ],
    };
    return of(stub);
  }
}
